// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { Shield, ChevronDown, ChevronRight, Filter, RotateCw } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import {
  ErrorBanner, LoadingState, EmptyState, formatTimestamp, relativeTime,
} from '../../components/workflow/StatePills'
import { usePaged, Pager, DEFAULT_PAGE_SIZE } from '../../components/Pager'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

interface Props { onScreenEnter: () => void }

const ACTIONS: { value: string; label: string }[] = [
  { value: '',                     label: 'All actions' },
  { value: 'rx.create',            label: 'Rx created' },
  { value: 'rx.transition',        label: 'Rx clinical transition' },
  { value: 'rx.payer_transition',  label: 'Rx payer transition' },
  { value: 'rx.fill',              label: 'Rx filled' },
  { value: 'refill.create',        label: 'Refill requested' },
  { value: 'refill.fulfill',       label: 'Refill fulfilled' },
  { value: 'refill.deny',          label: 'Refill denied' },
  { value: 'feedback.submit',      label: 'Feedback submitted' },
  { value: 'vote.submit',          label: 'Roadmap vote' },
]

// D-UI-2: what each audit column shows, for its header funnel.
const AUDIT_FUNNELS: FunnelColumn<api.AuditEntry>[] = [
  { key: 'when',   label: 'When',   value: e => e.occurred_at },
  { key: 'actor',  label: 'Actor',  value: e => e.actor_account },
  { key: 'action', label: 'Action', value: e => e.action },
  { key: 'entity', label: 'Entity', value: e => e.entity_type },
]

const ENTITY_TYPES: { value: string; label: string }[] = [
  { value: '',                 label: 'All entities' },
  { value: 'rx_item',          label: 'Prescriptions' },
  { value: 'refill_request',   label: 'Refill requests' },
  { value: 'feedback',         label: 'Feedback items' },
]

export default function AdminDashboard({ onScreenEnter }: Props) {
  const [items, setItems] = useState<api.AuditEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actor, setActor] = useState('')
  const [action, setAction] = useState('')
  const [entityType, setEntityType] = useState('')
  const [limit, setLimit] = useState(100)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [showResetDialog, setShowResetDialog] = useState(false)
  // The header funnels filter the whole loaded set; paging happens after.
  const fx = useTableFunnels(items, AUDIT_FUNNELS)
  const { page, setPage, pageSize, setPageSize, pageRows, total, totalPages } = usePaged(fx.rows, DEFAULT_PAGE_SIZE, `${action}|${entityType}|${actor}|${limit}|${fx.sig}`)
  const [resetBusy, setResetBusy] = useState(false)
  const [resetResult, setResetResult] = useState<api.DemoResetResponse | null>(null)

  useEffect(() => { onScreenEnter() }, [onScreenEnter])

  function reload() {
    setLoading(true); setError(null)
    api.getAuditLog({
      actor: actor || undefined,
      action: action || undefined,
      entity_type: entityType || undefined,
      limit,
    })
      .then(r => { setItems(r.items); setLoading(false) })
      .catch(e => { setError(friendlyError(e)); setLoading(false) })
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reload, [action, entityType, limit])

  async function doReset() {
    setResetBusy(true); setError(null)
    try {
      const r = await api.demoReset()
      setResetResult(r)
      reload()
    } catch (e: any) {
      setError(friendlyError(e, 'Reset failed'))
    } finally {
      setResetBusy(false)
    }
  }

  function toggle(id: number) {
    const next = new Set(expanded)
    next.has(id) ? next.delete(id) : next.add(id)
    setExpanded(next)
  }

  // Group counts for the strip
  const byAction = new Map<string, number>()
  for (const i of items) byAction.set(i.action, (byAction.get(i.action) ?? 0) + 1)

  return (
    <div style={{ maxWidth: 'var(--page-max, 1200px)', margin: '0 auto', padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Shield size={22} />
            <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Audit log</h1>
          </div>
          <p style={{ color: 'var(--color-text-soft)', margin: 0, fontSize: 14 }}>
            Append-only record of every state change. Per ADR-006, satisfies §164.312(b).
          </p>
        </div>
        <button onClick={() => setShowResetDialog(true)} style={resetBtn}>
          <RotateCw size={14} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />
          Reset demo data
        </button>
      </div>

      <div style={{ height: 16 }} />

      {resetResult && (
        <div role="status" style={{
          background: '#d1fae5', border: '1px solid #6ee7b7', color: '#065f46',
          padding: '10px 14px', borderRadius: 6, marginBottom: 12, fontSize: 14,
        }}>
          ✓ {resetResult.message} ·{' '}
          <button onClick={() => setResetResult(null)}
            style={{ background: 'transparent', color: '#065f46', textDecoration: 'underline', padding: 0, fontWeight: 600 }}>
            dismiss
          </button>
        </div>
      )}

      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

      {/* Filter bar */}
      <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 6, padding: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 12, color: 'var(--color-text-soft)' }}>
          <Filter size={12} /> Filters
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
          <select value={action} onChange={e => setAction(e.target.value)} style={input}>
            {ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
          <select value={entityType} onChange={e => setEntityType(e.target.value)} style={input}>
            {ENTITY_TYPES.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
          </select>
          <input
            type="text"
            placeholder="Filter by actor account…"
            value={actor}
            onChange={e => setActor(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && reload()}
            style={input}
          />
          <select value={limit} onChange={e => setLimit(parseInt(e.target.value))} style={input}>
            <option value={25}>Last 25</option>
            <option value={100}>Last 100</option>
            <option value={250}>Last 250</option>
            <option value={500}>Last 500</option>
          </select>
        </div>
      </div>

      {/* Action breakdown strip */}
      {byAction.size > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12, fontSize: 12 }}>
          {Array.from(byAction.entries()).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
            <button key={k} onClick={() => setAction(k === action ? '' : k)}
              style={{
                padding: '4px 10px', borderRadius: 12,
                background: k === action ? 'var(--color-primary)' : 'var(--color-surface-alt)',
                color: k === action ? 'white' : 'var(--color-text-soft)',
                border: '1px solid var(--color-border)', fontSize: 11,
              }}>
              {k} ({v})
            </button>
          ))}
        </div>
      )}

      {/* Audit table */}
      <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 6 }}>
        {loading && <LoadingState label="Loading audit log…" />}
        {!loading && items.length === 0 && <EmptyState message="No audit events match the current filters." />}
        {!loading && items.length > 0 && (
          <>
          <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} label="events" />
          {fx.chips}
          <table {...fx.tableProps} style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: 'var(--color-surface-alt)' }}>
              <tr>
                <th style={{ ...th, width: 30 }}></th>
                <th style={th}>When {fx.funnel('when')}</th>
                <th style={th}>Actor {fx.funnel('actor')}</th>
                <th style={th}>Action {fx.funnel('action')}</th>
                <th style={th}>Entity {fx.funnel('entity')}</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map(e => {
                const isOpen = expanded.has(e.id)
                return (
                  <>
                    <tr key={e.id} style={{ borderBottom: '1px solid var(--color-border)', cursor: 'pointer' }}
                      onClick={() => toggle(e.id)}>
                      <td style={td}>{isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                      <td style={td}>
                        <div>{relativeTime(e.occurred_at)}</div>
                        <div style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>{formatTimestamp(e.occurred_at)}</div>
                      </td>
                      <td style={td}>
                        <div>{e.actor_account}</div>
                        <div style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>{e.actor_role}</div>
                      </td>
                      <td style={td}>
                        <code style={{ fontSize: 12, background: 'var(--color-surface-alt)', padding: '2px 6px', borderRadius: 3 }}>
                          {e.action}
                        </code>
                      </td>
                      <td style={td}>
                        <div style={{ fontSize: 12 }}>{e.entity_type}</div>
                        <div style={{ fontSize: 11, color: 'var(--color-text-mute)', fontFamily: 'monospace' }}>
                          {e.entity_id.length > 8 ? e.entity_id.slice(0, 8) + '…' : e.entity_id}
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      // Expanded detail must stay distinct from the zebra stripe, which now
                      // uses --color-surface-alt on every other row (index.css).
                      <tr key={`${e.id}-detail`} style={{ background: 'var(--color-primary-soft)', borderBottom: '1px solid var(--color-border)' }}>
                        <td colSpan={5} style={{ padding: 14 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                            <Block label="Before"   content={e.before_state} />
                            <Block label="After"    content={e.after_state} />
                            <Block label="Metadata" content={e.metadata} />
                          </div>
                          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--color-text-mute)' }}>
                            audit_id: {e.id} · session: {e.actor_session ?? '—'} · ip: {e.actor_ip ?? '—'}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                )
              })}
            </tbody>
          </table>
          <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} showPageSize={false} label="events" />
          </>
        )}
      </div>

      <p style={{ marginTop: 12, fontSize: 12, color: 'var(--color-text-mute)' }}>
        Showing {items.length} of last {limit} audit events. Click any row to expand the before/after diff.
      </p>

      {showResetDialog && (
        <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) setShowResetDialog(false) }}>
          <div style={dialogStyle}>
            <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 600 }}>Reset demo data?</h2>
            <p style={{ fontSize: 13, color: 'var(--color-text-soft)', marginTop: 0 }}>
              This will:
            </p>
            <ul style={{ fontSize: 13, color: 'var(--color-text)', marginTop: 4, paddingLeft: 20 }}>
              <li>Delete all prescriptions created during demo sessions</li>
              <li>Delete all fills, refill requests, and state log entries linked to demo Rx</li>
              <li>Restore seeded prescriptions to their original starting states</li>
              <li>Preserve the audit log (per ADR-006, append-only)</li>
            </ul>
            <p style={{ fontSize: 12, color: 'var(--color-text-mute)', marginTop: 12 }}>
              Server-name guard ensures this only runs against the mockup database.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button onClick={() => setShowResetDialog(false)} disabled={resetBusy}
                style={{ padding: '8px 14px', background: 'transparent', border: '1px solid var(--color-border)', borderRadius: 4, fontSize: 13, color: 'var(--color-text-soft)' }}>
                Cancel
              </button>
              <button onClick={async () => { await doReset(); setShowResetDialog(false) }} disabled={resetBusy} style={resetBtn}>
                {resetBusy ? 'Resetting…' : 'Yes, reset demo data'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Block({ label, content }: { label: string; content: string | null }) {
  let formatted: string = content ?? '(none)'
  if (content) {
    try { formatted = JSON.stringify(JSON.parse(content), null, 2) }
    catch { formatted = content }
  }
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-soft)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
        {label}
      </div>
      <pre style={{
        margin: 0, padding: 8, background: 'var(--color-surface)',
        border: '1px solid var(--color-border)', borderRadius: 4,
        fontSize: 11, fontFamily: 'ui-monospace, monospace',
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        maxHeight: 240, overflow: 'auto',
      }}>{formatted}</pre>
    </div>
  )
}

const th: React.CSSProperties = { textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 600, color: 'var(--color-text-soft)', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid var(--color-border)' }
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 13, verticalAlign: 'top' }
const input: React.CSSProperties = { padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 4, fontSize: 13, background: 'var(--color-surface)', fontFamily: 'inherit' }
const resetBtn: React.CSSProperties = { padding: '8px 14px', background: 'transparent', border: '1px solid #dc2626', color: '#dc2626', borderRadius: 4, fontSize: 13, fontWeight: 500 }
const overlayStyle: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(28,25,23,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 200, padding: 16 }
const dialogStyle: React.CSSProperties = { background: 'var(--color-surface)', borderRadius: 8, padding: 20, width: '100%', maxWidth: 480, boxShadow: 'var(--shadow-lg)' }
